import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import api, { formatApiError } from '@/lib/api';
import { toast } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { ExternalLink, Link2, Pencil, Trash2 } from 'lucide-react';
import AdminPageShell from '@/components/admin/AdminPageShell';
import EmailApprovalDialog from '@/components/emails/EmailApprovalDialog';
import {
  APT_STATUS_COLORS,
  formatAptRent,
  formatAptRooms,
  formatAptSize,
  pmLabel,
  sourceLabel,
  statusLabel,
} from '@/lib/apartmentFormat';

export default function AdminApartmentDetailPage() {
  const { aptId } = useParams();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [apt, setApt] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [visits, setVisits] = useState([]);
  const [matchedUser, setMatchedUser] = useState(null);
  const [applicants, setApplicants] = useState([]);
  const [matchUserId, setMatchUserId] = useState('');
  const [matchMode, setMatchMode] = useState('propose');
  const [matchSaving, setMatchSaving] = useState(false);
  const [showMatch, setShowMatch] = useState(searchParams.get('match') === '1');
  const [activeTab, setActiveTab] = useState('overview');
  const [emailDraft, setEmailDraft] = useState(null);
  const [draftQueue, setDraftQueue] = useState([]);
  const [eligibleRows, setEligibleRows] = useState([]);
  const [eligibleSelected, setEligibleSelected] = useState({});
  const [eligibleLoading, setEligibleLoading] = useState(false);
  const [bulkProposeSaving, setBulkProposeSaving] = useState(false);

  const load = useCallback(async () => {
    if (!aptId) return;
    setLoading(true);
    try {
      const [profileRes, appsRes] = await Promise.all([
        api.get(`/apartments/${aptId}`),
        api.get('/applications'),
      ]);
      const data = profileRes.data || {};
      setApt(data.apartment);
      setProposals(data.proposals || []);
      setVisits(data.visits || []);
      setMatchedUser(data.matched_user || null);
      if ((data.apartment || {}).status === 'available') {
        setEligibleLoading(true);
        api.get(`/apartments/${aptId}/eligible-applicants`)
          .then((r) => {
            const rows = r.data?.eligible || [];
            setEligibleRows(rows);
            const sel = {};
            rows.forEach((row) => { sel[row.application_id] = true; });
            setEligibleSelected(sel);
          })
          .catch(() => setEligibleRows([]))
          .finally(() => setEligibleLoading(false));
      } else {
        setEligibleRows([]);
        setEligibleSelected({});
      }
      const apps = appsRes.data || [];
      setApplicants(
        apps
          .filter((a) => a.user_id)
          .map((a) => ({
            user_id: a.user_id,
            application_id: a.id,
            status: a.status || 'draft',
            label: a.user?.name || a.user?.email || a.user_id,
          }))
      );
    } catch {
      toast.error('Failed to load apartment');
      navigate('/admin/apartments');
    } finally {
      setLoading(false);
    }
  }, [aptId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const selectedApplicant = applicants.find((a) => a.user_id === matchUserId);

  const handleProposeListing = async () => {
    if (!matchUserId || !selectedApplicant) {
      toast.error(t('admin.apt.selectTenant'));
      return;
    }
    if (selectedApplicant.status === 'draft') {
      toast.error(t('admin.apt.proposeBlockedDraft'));
      return;
    }
    setMatchSaving(true);
    try {
      const res = await api.post(`/applications/${selectedApplicant.application_id}/listing-proposals`, {
        apartment_id: aptId,
        message: null,
      });
      toast.success(t('admin.email.proposedNeedsSend') || t('admin.apt.proposed'));
      const pending = res.data?.pending_email_draft;
      const pendingError = res.data?.pending_email_draft_error;
      if (pending?.id) setEmailDraft(pending);
      else if (pendingError) toast.error(`${t('admin.apt.emailDraftError')}: ${pendingError}`);
      setShowMatch(false);
      setMatchUserId('');
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || t('admin.apt.proposeFailed'));
    } finally {
      setMatchSaving(false);
    }
  };

  const handleMatch = async () => {
    if (!matchUserId || !selectedApplicant) {
      toast.error(t('admin.apt.selectTenant'));
      return;
    }
    setMatchSaving(true);
    try {
      const res = await api.post(`/apartments/${aptId}/match`, { user_id: matchUserId });
      toast.success(t('admin.email.matchedNeedsSend') || t('admin.apt.matched'));
      const pending = res.data?.pending_email_draft;
      const pendingError = res.data?.pending_email_draft_error;
      if (pending?.id) setEmailDraft(pending);
      else if (pendingError) toast.error(`${t('admin.apt.emailDraftError')}: ${pendingError}`);
      setShowMatch(false);
      setMatchUserId('');
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || t('admin.apt.matchFailed'));
    } finally {
      setMatchSaving(false);
    }
  };


  const handleProposeEligible = async () => {
    const ids = Object.keys(eligibleSelected).filter((id) => eligibleSelected[id]);
    if (!ids.length) {
      toast.error(t('admin.bulkMatch.noSelection'));
      return;
    }
    setBulkProposeSaving(true);
    try {
      const res = await api.post('/admin/bulk-listing-proposals', {
        items: ids.map((application_id) => ({ apartment_id: aptId, application_id })),
        message: null,
      });
      const created = res.data?.created?.length || 0;
      const drafts = (res.data?.pending_email_drafts || []).filter((d) => d?.id);
      if (created) toast.success(`${created} ${t('admin.bulkMatch.proposalsCreated')}`);
      load();
      if (drafts.length) {
        setEmailDraft(drafts[0]);
        setDraftQueue(drafts.slice(1));
      }
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || t('admin.bulkMatch.failed'));
    } finally {
      setBulkProposeSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this apartment?')) return;
    try {
      await api.delete(`/apartments/${aptId}`);
      toast.success('Deleted');
      navigate('/admin/apartments');
    } catch {
      toast.error('Failed to delete');
    }
  };

  if (loading || !apt) {
    return (
      <AdminPageShell testId="admin-apartment-detail-page" maxWidth="max-w-4xl">
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#0B3D2E] border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminPageShell>
    );
  }

  return (
    <AdminPageShell
      testId="admin-apartment-detail-page"
      maxWidth="max-w-4xl"
      title={apt.title}
      breadcrumbs={[
        { label: t('admin.apartments'), to: '/admin/apartments' },
        { label: apt.title },
      ]}
    >
        <div className="flex flex-wrap gap-2 mb-4">
          <Badge className={`${APT_STATUS_COLORS[apt.status] || ''} border-0`}>{statusLabel(apt.status, t)}</Badge>
          {apt.crm_needs_attention && (
            <Badge className="bg-amber-50 text-amber-800 border-0 text-[10px]">{t('admin.apt.kpiAttention')}</Badge>
          )}
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs border-[#0B3D2E] text-[#0B3D2E]"
            onClick={() => navigate(`/admin/apartments/${aptId}/edit`)}
            data-testid="edit-apartment-btn"
          >
            <Pencil className="w-3 h-3 mr-1" /> {t('admin.apt.edit')}
          </Button>
          {apt.external_url && (
            <Button size="sm" variant="outline" className="h-7 text-xs" asChild>
              <a href={apt.external_url} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="w-3 h-3 mr-1" /> {t('admin.apt.viewListing')}
              </a>
            </Button>
          )}
          {apt.status === 'available' && (
            <Button size="sm" variant="outline" className="h-7 text-xs border-[#0B3D2E] text-[#0B3D2E]" onClick={() => setShowMatch(true)}>
              <Link2 className="w-3 h-3 mr-1" /> {t('admin.match')}
            </Button>
          )}
          <Button size="sm" variant="ghost" className="h-7 text-xs text-red-600 ml-auto" onClick={handleDelete}>
            <Trash2 className="w-3 h-3 mr-1" /> {t('common.delete')}
          </Button>
        </div>

        {showMatch && apt.status === 'available' && (
          <div className="bg-white border border-[#2B2B2B]/10 rounded-lg p-4 mb-4 space-y-3">
            <p className="text-xs text-[#5a5a5a]">{t('admin.apt.matchHelp')}</p>
            {applicants.length === 0 ? (
              <p className="text-sm text-[#5a5a5a]">{t('admin.apt.noApplicants')}</p>
            ) : (
              <Select value={matchUserId} onValueChange={setMatchUserId}>
                <SelectTrigger className="h-9 text-xs" data-testid="match-user-select"><SelectValue placeholder={t('admin.apt.selectTenant')} /></SelectTrigger>
                <SelectContent>
                  {applicants.map((a) => (
                    <SelectItem key={a.user_id} value={a.user_id} className="text-xs">
                      {a.label} ({a.status})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant={matchMode === 'propose' ? 'default' : 'outline'}
                className={matchMode === 'propose' ? 'bg-[#0B3D2E] text-white text-xs' : 'text-xs'}
                onClick={() => setMatchMode('propose')}
              >
                {t('admin.apt.modePropose')}
              </Button>
              <Button
                size="sm"
                variant={matchMode === 'final' ? 'default' : 'outline'}
                className={matchMode === 'final' ? 'bg-[#0B3D2E] text-white text-xs' : 'text-xs'}
                onClick={() => setMatchMode('final')}
              >
                {t('admin.apt.modeFinalMatch')}
              </Button>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                className="bg-[#0B3D2E] text-white text-xs"
                onClick={matchMode === 'final' ? handleMatch : handleProposeListing}
                disabled={matchSaving || !matchUserId || applicants.length === 0}
                data-testid="confirm-match-btn"
              >
                {matchSaving ? t('common.loading') : matchMode === 'final' ? t('admin.apt.confirmMatch') : t('admin.apt.confirmPropose')}
              </Button>
              <Button size="sm" variant="ghost" className="text-xs" onClick={() => setShowMatch(false)}>{t('common.cancel')}</Button>
            </div>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-white border border-[#2B2B2B]/10 p-1 flex flex-wrap h-auto gap-1">
            <TabsTrigger value="overview" className="text-xs data-[state=active]:bg-[#0B3D2E] data-[state=active]:text-white">{t('admin.apt.tabOverview')}</TabsTrigger>
            <TabsTrigger value="applications" className="text-xs data-[state=active]:bg-[#0B3D2E] data-[state=active]:text-white">
              {t('admin.apt.tabApplications')} ({proposals.length})
            </TabsTrigger>
            <TabsTrigger value="visits" className="text-xs data-[state=active]:bg-[#0B3D2E] data-[state=active]:text-white">
              {t('admin.apt.tabVisits')} ({visits.length})
            </TabsTrigger>
            <TabsTrigger value="activity" className="text-xs data-[state=active]:bg-[#0B3D2E] data-[state=active]:text-white">{t('admin.apt.tabActivity')}</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <div className="bg-white border border-[#2B2B2B]/10 rounded-lg p-4 sm:p-6 space-y-4 text-sm">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <Field label={t('admin.apt.colStatus')} value={statusLabel(apt.status, t)} />
                <Field label={t('admin.apt.colRent')} value={`${formatAptRent(apt)}/month`} />
                <Field label={t('admin.apt.colSize')} value={formatAptSize(apt)} />
                <Field label={t('admin.apt.colRooms')} value={formatAptRooms(apt)} />
                <Field label={t('admin.apt.colManager')} value={pmLabel(apt.property_manager)} />
                <Field label={t('admin.apt.colSource')} value={sourceLabel(apt.source, t)} />
              </div>
              <div>
                <p className="text-[10px] uppercase text-[#5a5a5a] mb-1">{t('admin.apt.fieldAddress')}</p>
                <p className="text-[#2B2B2B]">{apt.address || '—'}</p>
                {apt.neighborhood && <p className="text-xs text-[#5a5a5a] mt-0.5">{apt.neighborhood}, Geneva</p>}
              </div>
              {matchedUser && (
                <div>
                  <p className="text-[10px] uppercase text-[#5a5a5a] mb-1">{t('admin.apt.tenant')}</p>
                  <p className="text-emerald-700">{matchedUser.name || matchedUser.email}</p>
                </div>
              )}
              {apt.description && (
                <div>
                  <p className="text-[10px] uppercase text-[#5a5a5a] mb-1">{t('admin.apt.fieldDescription')}</p>
                  <p className="text-[#2B2B2B] whitespace-pre-wrap">{apt.description}</p>
                </div>
              )}
              {apt.status === 'available' && (
                <div className="border-t border-[#2B2B2B]/10 pt-4" data-testid="eligible-applicants-section">
                  <h3 className="font-medium text-[#2B2B2B] mb-1">{t('admin.apt.eligibleTitle')}</h3>
                  <p className="text-xs text-[#5a5a5a] mb-3">{t('admin.apt.eligibleHelp')}</p>
                  {eligibleLoading ? (
                    <p className="text-xs text-[#5a5a5a]">{t('common.loading')}</p>
                  ) : eligibleRows.length === 0 ? (
                    <p className="text-xs text-[#5a5a5a]">{t('admin.bulkMatch.noEligible')}</p>
                  ) : (
                    <>
                      <ul className="divide-y divide-[#2B2B2B]/5 border border-[#2B2B2B]/10 rounded-lg mb-3">
                        {eligibleRows.map((row) => (
                          <li key={row.application_id} className="flex items-center gap-3 px-3 py-2 text-sm">
                            <Checkbox
                              checked={!!eligibleSelected[row.application_id]}
                              onCheckedChange={(v) => setEligibleSelected((prev) => ({
                                ...prev,
                                [row.application_id]: !!v,
                              }))}
                            />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{row.name || row.email}</p>
                              <p className="text-[10px] text-[#5a5a5a]">
                                {t('admin.bulkMatch.score')}: {Math.round(row.score_total || 0)}
                                {row.income_ratio > 0 && <> &middot; {row.income_ratio}x</>}
                              </p>
                            </div>
                            <Badge className="bg-slate-100 text-slate-600 border-0 text-[10px] capitalize">{row.status}</Badge>
                          </li>
                        ))}
                      </ul>
                      <Button
                        size="sm"
                        className="bg-[#0B3D2E] text-white text-xs"
                        disabled={bulkProposeSaving}
                        onClick={handleProposeEligible}
                        data-testid="propose-eligible-btn"
                      >
                        {bulkProposeSaving ? t('common.loading') : t('admin.apt.proposeSelected')}
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="applications">
            <div className="bg-white border border-[#2B2B2B]/10 rounded-lg divide-y divide-[#2B2B2B]/5">
              {proposals.length === 0 && (
                <p className="text-sm text-[#5a5a5a] p-6 text-center">{t('admin.apt.noApplications')}</p>
              )}
              {proposals.map((p) => {
                const name = p.user?.name || p.user?.email || 'Applicant';
                const appId = p.application?.id || p.application_id;
                return (
                  <div key={p.id} className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div>
                      <p className="font-medium text-[#2B2B2B]">{name}</p>
                      <p className="text-xs text-[#5a5a5a] capitalize">{p.status || 'proposed'}</p>
                    </div>
                    {appId && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs border-[#0B3D2E] text-[#0B3D2E] w-full sm:w-auto"
                        onClick={() => navigate(`/admin?tab=applications&application_id=${appId}`)}
                      >
                        {t('admin.review')}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="visits">
            <div className="bg-white border border-[#2B2B2B]/10 rounded-lg divide-y divide-[#2B2B2B]/5">
              {visits.length === 0 && (
                <p className="text-sm text-[#5a5a5a] p-6 text-center">{t('admin.apt.noVisits')}</p>
              )}
              {visits.map((v) => (
                <div key={v.id} className="p-4 flex justify-between items-start gap-2">
                  <div>
                    <p className="font-medium text-[#2B2B2B]">{v.user?.name || v.user?.email || '—'}</p>
                    <p className="text-xs text-[#5a5a5a]">{v.requested_date || '—'}</p>
                  </div>
                  <Badge className="border-0 text-[10px] capitalize">{v.status}</Badge>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="activity">
            <div className="bg-white border border-[#2B2B2B]/10 rounded-lg p-6 text-sm text-[#5a5a5a] text-center">
              {t('admin.apt.activityPlaceholder')}
            </div>
          </TabsContent>
        </Tabs>
      <EmailApprovalDialog
        draft={emailDraft}
        open={!!emailDraft}
        onClose={() => {
          if (draftQueue.length) {
            setEmailDraft(draftQueue[0]);
            setDraftQueue((q) => q.slice(1));
          } else {
            setEmailDraft(null);
            load();
          }
        }}
      />
      </AdminPageShell>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase text-[#5a5a5a]">{label}</p>
      <p className="text-[#2B2B2B] font-medium mt-0.5">{value}</p>
    </div>
  );
}
